import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import Chart from "./Chart";
import KPITile from "./KPITile";
import { WidgetNoteFrame } from "./WidgetNote";
import EmptyState from "./states/EmptyState";
import ErrorState from "./states/ErrorState";
import Skeleton from "./states/Skeleton";
import {
  LOB_SHORT,
  activityTrend,
  buildRecords,
  completeMonthsOnly,
  dailyActivityRate,
  dailyCounts,
  detailRecords,
  divisionActivity,
  filterDivisions,
  heatBucket,
  inventorySnapshot,
  kpiTile,
  latestActiveDate,
  memo,
  monthLabel,
  monthlySummary,
  normalizeContainerConfig,
  selectedMonth,
  siteColumnState,
  sizeHeatmap,
  sizeMix,
  workOrderLogCsv,
  type ContainerConfig,
  type ContainerDetailRecord,
  type ContainerLob,
  type ContainerRecord,
} from "../lib/containerInventory.mjs";
import { fetchContainerAggregates, fetchContainerDetail } from "../lib/containerInventoryData";
import { useGlobalFilters } from "../lib/globalFilters";
import { type ManifestWidget } from "../lib/manifest";
import { declaredFeedGap } from "../lib/missingFeed.mjs";
import { GATE_COPY } from "../lib/sanityGate";
import { useTenantId } from "../lib/tenant";
import { hasTileInfo, noteSections } from "../lib/widgetNote.mjs";
import { widgetDomain } from "../lib/widgetDomain.mjs";

/**
 * Containers tab renderer (wave 1: the client's Container Inventory tool).
 *
 * One component, one `view` per manifest widget. Every view reads the same
 * grouped counts (containerInventoryData.ts, fetched once per page) and every
 * number comes out of containerInventory.mjs, so the tiles, the snapshot, the
 * summary and the memo cannot disagree with one another.
 *
 * Scope: the page's division chip, and the page's month when one is chosen
 * (otherwise the last complete calendar month). The trend and analytics views
 * read every month loaded, as the client's tool does, and say so.
 */

// The feed the site-level views wait on (MER-855 adds it to the work order
// sync). Named in the tile the way a blocked board tile names its feed.
const SITE_FEED = "the site id and name on each work order";

const card: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: "14px 16px",
};
const th: CSSProperties = {
  textAlign: "right",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "var(--text-2)",
  padding: "6px 8px",
  borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap",
};
const td: CSSProperties = {
  textAlign: "right",
  fontSize: 13,
  padding: "5px 8px",
  borderBottom: "1px solid var(--border-subtle)",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};
const left: CSSProperties = { textAlign: "left" };

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  return n.toLocaleString("en-US");
}
function fmtSigned(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  return `${n > 0 ? "+" : ""}${n.toLocaleString("en-US")}`;
}
function fmtPct(fraction: number | null | undefined, digits = 0): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return "-";
  return `${(fraction * 100).toFixed(digits)}%`;
}
function fmtRate(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  return n.toFixed(1);
}
function signColor(n: number): string | undefined {
  if (n > 0) return "var(--flag-green)";
  if (n < 0) return "var(--flag-red)";
  return undefined;
}

function Table(props: { head: ReactNode[]; rows: ReactNode[][]; foot?: ReactNode[] }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {props.head.map((h, i) => (
              <th key={i} style={i === 0 ? { ...th, ...left } : th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} style={j === 0 ? { ...td, ...left } : td}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {props.foot ? (
          <tfoot>
            <tr>
              {props.foot.map((c, j) => (
                <td key={j} style={{ ...(j === 0 ? { ...td, ...left } : td), fontWeight: 700 }}>
                  {c}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function Signed(props: { n: number }) {
  return <span style={{ color: signColor(props.n), fontWeight: 600 }}>{fmtSigned(props.n)}</span>;
}

function download(filename: string, body: string, type: string) {
  const blob = new Blob([body], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const buttonStyle: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-card)",
  color: "var(--text-1)",
  cursor: "pointer",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; records: ContainerRecord[] };

function useContainerRecords(cfg: ContainerConfig | null, divisions: string[]): LoadState {
  const tenantId = useTenantId();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  useEffect(() => {
    if (!tenantId || !cfg) return;
    let alive = true;
    setState({ kind: "loading" });
    fetchContainerAggregates(tenantId, cfg).then(
      (rows) => {
        if (alive) setState({ kind: "ready", records: buildRecords(rows, cfg) });
      },
      (err: unknown) => {
        if (alive)
          setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      },
    );
    return () => {
      alive = false;
    };
  }, [tenantId, cfg]);
  return useMemo(() => {
    if (state.kind !== "ready") return state;
    return { kind: "ready", records: filterDivisions(state.records, divisions) };
  }, [state, divisions]);
}

type DetailState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; columnsPresent: boolean; detail: ContainerDetailRecord[] };

function useContainerDetail(
  cfg: ContainerConfig | null,
  month: string | null,
  divisions: string[],
  enabled: boolean,
): DetailState {
  const tenantId = useTenantId();
  const [state, setState] = useState<DetailState>({ kind: "loading" });
  useEffect(() => {
    if (!enabled || !tenantId || !cfg || !month) return;
    let alive = true;
    setState({ kind: "loading" });
    fetchContainerDetail(tenantId, cfg, month).then(
      (res) => {
        if (alive) {
          setState({
            kind: "ready",
            columnsPresent: res.columnsPresent,
            detail: detailRecords(res.rows, cfg),
          });
        }
      },
      (err: unknown) => {
        if (alive)
          setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      },
    );
    return () => {
      alive = false;
    };
  }, [enabled, tenantId, cfg, month]);
  return useMemo(() => {
    if (state.kind !== "ready") return state;
    return { ...state, detail: filterDivisions(state.detail, divisions) };
  }, [state, divisions]);
}

export default function ContainerInventoryWidget(props: { widget: ManifestWidget }) {
  const { widget } = props;
  const filters = useGlobalFilters();
  const cfg = useMemo(
    () => normalizeContainerConfig(widget.container_inventory),
    [widget.container_inventory],
  );
  const divisionKey = filters.divisions.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const divisions = useMemo(() => filters.divisions, [divisionKey]);
  const month = useMemo(() => selectedMonth(filters.month, new Date()), [filters.month]);
  const view = widget.view ?? "";
  const needsDetail = view === "work_order_detail" || view === "daily_tracker";
  const loaded = useContainerRecords(cfg, divisions);
  // The page's service chip narrows the all-line views to the lines picked.
  // A per-line view (container_lob) declares its scope and keeps its line.
  const serviceKey = filters.services.join(",");
  const load = useMemo((): LoadState => {
    if (loaded.kind !== "ready" || widget.container_lob || filters.services.length === 0) {
      return loaded;
    }
    const picked = new Set(filters.services);
    return { kind: "ready", records: loaded.records.filter((r) => picked.has(r.lob)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, widget.container_lob, serviceKey]);
  const detail = useContainerDetail(cfg, month, divisions, needsDetail);

  const sections = noteSections({
    measures: widget.what_it_shows,
    note: widget.widget_note,
    action: widget.action,
    good: widget.good,
  });
  const note = hasTileInfo(sections) ? sections : [];
  const domain = widgetDomain(widget);
  const frame = (node: ReactNode, opts: { heading?: boolean } = {}) => {
    const body =
      opts.heading === false ? (
        node
      ) : (
        <div style={card}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)" }}>
              {widget.name}
            </div>
            {widget.what_it_shows ? (
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 3, lineHeight: 1.45 }}>
                {widget.what_it_shows}
              </div>
            ) : null}
          </div>
          {node}
        </div>
      );
    return note.length > 0 ? (
      <WidgetNoteFrame sections={note} label={widget.name} domain={domain}>
        {body}
      </WidgetNoteFrame>
    ) : (
      <>{body}</>
    );
  };

  if (!cfg) {
    return (
      <EmptyState
        title={widget.name}
        caption="Container work types are not configured for this customer yet."
      />
    );
  }
  if (load.kind === "loading") return <Skeleton label={widget.name} />;
  if (load.kind === "error") {
    return <EmptyState title={widget.name} caption={GATE_COPY} />;
  }
  // In-progress months never reach a view (MER-553 / MER-693): every trend,
  // table, heatmap and prior-month comparison reads closed months only.
  const records = completeMonthsOnly(load.records, new Date());
  const monthLabelText = month ? monthLabel(month) : "";
  const scopeNote = (text: string) => (
    <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>{text}</div>
  );

  if (records.length === 0) {
    const chipEmptied =
      !widget.container_lob &&
      filters.services.length > 0 &&
      loaded.kind === "ready" &&
      loaded.records.length > 0;
    return (
      <EmptyState
        title={widget.name}
        caption={chipEmptied ? "No container work on the selected service lines." : GATE_COPY}
      />
    );
  }

  switch (view) {
    case "kpi": {
      const tile = month ? kpiTile(records, widget.container_metric ?? "", month) : null;
      if (!tile) return <ErrorState message={`Unknown container metric on ${widget.id}`} />;
      const signed = tile.change_abs !== null;
      const periodNote =
        tile.value === null
          ? `${monthLabelText}: no container work orders loaded`
          : signed
            ? `${monthLabelText} · ${fmtSigned(tile.change_abs)} vs ${monthLabel(tile.prior_month ?? "")}`
            : `${monthLabelText}${tile.prior !== null ? ` · ${fmtInt(tile.prior)} in ${monthLabel(tile.prior_month ?? "")}` : ""}`;
      return (
        <KPITile
          title={widget.name}
          value={tile.value}
          format="integer"
          delta={tile.change_pct}
          caption={widget.what_it_shows}
          isGated={tile.value === null}
          gateCopy={GATE_COPY}
          periodNote={periodNote}
          domain={domain}
          sections={note}
        />
      );
    }

    case "inventory_snapshot": {
      const lob = (widget.container_lob ?? "roll_off") as ContainerLob;
      const cards = month ? inventorySnapshot(records, lob, month) : [];
      if (cards.length === 0) {
        return frame(
          scopeNote(`${monthLabelText}: no ${LOB_SHORT[lob]} deliveries or removals loaded.`),
        );
      }
      return frame(
        <>
          {scopeNote(
            `${monthLabelText}. Net Yard = removals minus deliveries: positive means containers came back to the yard.`,
          )}
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            }}
          >
            {cards.map((c) => (
              <div
                key={c.division}
                style={{
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 6,
                  }}
                >
                  <span>{c.division}</span>
                  <span style={{ color: "var(--text-2)", fontSize: 11 }}>{LOB_SHORT[lob]}</span>
                </div>
                {c.rows.map((r) => (
                  <div
                    key={r.size}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      padding: "3px 0",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{r.size}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        {r.deliveries} del · {r.removals} rem
                      </div>
                    </div>
                    <Signed n={r.net_yard} />
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: "1px solid var(--border-subtle)",
                    marginTop: 6,
                    paddingTop: 6,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  <span>Net Yard Total</span>
                  <Signed n={c.total.net_yard} />
                </div>
              </div>
            ))}
          </div>
        </>,
      );
    }

    case "monthly_summary": {
      const s = monthlySummary(records);
      return frame(
        <>
          {scopeNote("Every month loaded. Net RO Yard = RO removals minus RO deliveries.")}
          <Table
            head={["Month", "RO Del", "RO Rem", "FL Del", "FL Rem", "Total", "Net RO Yard"]}
            rows={s.rows.map((r) => [
              monthLabel(r.month),
              fmtInt(r.ro_delivery),
              fmtInt(r.ro_removal),
              fmtInt(r.fl_delivery),
              fmtInt(r.fl_removal),
              fmtInt(r.total),
              <Signed key="n" n={r.net_ro_yard} />,
            ])}
            foot={[
              "Total",
              fmtInt(s.totals.ro_delivery),
              fmtInt(s.totals.ro_removal),
              fmtInt(s.totals.fl_delivery),
              fmtInt(s.totals.fl_removal),
              fmtInt(s.totals.total),
              <Signed key="n" n={s.totals.net_ro_yard} />,
            ]}
          />
        </>,
      );
    }

    case "activity_trend": {
      const t = activityTrend(records);
      const colors = ["#0F6B5C", "#5FB7A5", "#1F4E79", "#7FA7D6"];
      return frame(
        <Chart
          type="line"
          name={widget.name}
          format="integer"
          xAxisGranularity="category"
          height={240}
          data={{
            labels: t.months.map(monthLabel),
            datasets: t.series.map((s, i) => ({
              label: s.label,
              data: s.values,
              borderColor: colors[i % colors.length],
              backgroundColor: colors[i % colors.length],
              tension: 0.2,
            })),
          }}
        />,
      );
    }

    case "division_activity": {
      const rows = divisionActivity(records);
      return frame(
        <>
          {scopeNote(
            "Every month loaded. Share = the division's work orders over the line's total.",
          )}
          <Table
            head={["Division", "RO Work Orders", "RO Share", "FL Work Orders", "FL Share"]}
            rows={rows.map((r) => [
              r.division,
              fmtInt(r.ro_work_orders),
              fmtPct(r.ro_share),
              fmtInt(r.fl_work_orders),
              fmtPct(r.fl_share),
            ])}
          />
        </>,
      );
    }

    case "size_heatmap": {
      const lob = (widget.container_lob ?? "roll_off") as ContainerLob;
      const h = sizeHeatmap(records, lob);
      if (h.rows.length === 0) return frame(scopeNote(`No ${LOB_SHORT[lob]} work orders loaded.`));
      const shade = (v: number) => {
        const b = heatBucket(v, h.max);
        return b === 0 ? undefined : `rgba(15, 107, 92, ${0.08 + b * 0.14})`;
      };
      return frame(
        <Table
          head={["Size", ...h.months.map(monthLabel), "Total"]}
          rows={h.rows.map((r) => [
            r.size,
            ...r.cells.map((v, i) => (
              <span
                key={i}
                style={{
                  display: "inline-block",
                  minWidth: 32,
                  padding: "1px 4px",
                  borderRadius: 4,
                  background: shade(v),
                }}
              >
                {v ? fmtInt(v) : "-"}
              </span>
            )),
            <strong key="t">{fmtInt(r.total)}</strong>,
          ])}
          foot={["Total", ...h.colTotals.map(fmtInt), fmtInt(h.total)]}
        />,
      );
    }

    case "daily_activity_rate": {
      const d = dailyActivityRate(records);
      return frame(
        <>
          {scopeNote(
            "Work orders per active day. An active day is a scheduled date with any container work that month, not a calendar day.",
          )}
          <Table
            head={["Type", ...d.months.map(monthLabel), "Avg per Day"]}
            rows={d.rows.map((r) => [
              r.label,
              ...r.cells.map((c) => fmtRate(c.rate)),
              <strong key="a">{fmtRate(r.rate)}</strong>,
            ])}
          />
        </>,
      );
    }

    case "size_mix": {
      const lobs = (["roll_off", "front_load"] as ContainerLob[]).filter((l) => cfg[l]);
      return frame(
        <>
          {scopeNote("Every month loaded. Share of each line's work orders by container size.")}
          {lobs.map((lob) => (
            <div key={lob} style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "var(--text-2)",
                  marginBottom: 6,
                }}
              >
                {LOB_SHORT[lob]} containers
              </div>
              {sizeMix(records, lob).map((r) => (
                <div key={r.size} style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span>{r.size}</span>
                    <span>
                      <strong>{fmtPct(r.share)}</strong>{" "}
                      <span style={{ color: "var(--text-muted)" }}>{fmtInt(r.count)}</span>
                    </span>
                  </div>
                  <div style={{ height: 6, background: "var(--border-subtle)", borderRadius: 3 }}>
                    <div
                      style={{
                        width: fmtPct(r.share),
                        height: 6,
                        background: "var(--brand-teal)",
                        borderRadius: 3,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>,
      );
    }

    case "memo_activity": {
      if (!month) return frame(scopeNote("No month selected."));
      const m = memo(records, month);
      const util = (u: number | null) => (u === null ? "n/a" : `${u}%`);
      const rows = [...m.divisions, m.combined];
      return frame(
        <>
          <div style={{ fontSize: 13, marginBottom: 8 }}>{m.summary}</div>
          <Table
            head={[
              "Division",
              "RO Del",
              "RO Rem",
              "RO Utilization",
              "FL Del",
              "FL Rem",
              "Net FL Placed",
              "Total",
            ]}
            rows={rows.map((r) => [
              r.division === "Combined" ? <strong key="c">Combined</strong> : r.division,
              fmtInt(r.ro_delivery),
              fmtInt(r.ro_removal),
              util(r.ro_utilization),
              fmtInt(r.fl_delivery),
              fmtInt(r.fl_removal),
              <Signed key="n" n={r.net_fl_placed} />,
              fmtInt(r.total),
            ])}
          />
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              style={buttonStyle}
              onClick={() =>
                download(
                  `container-memo-${month}.doc`,
                  memoDocument(
                    m,
                    rows.map((r) => ({ ...r, util: util(r.ro_utilization) })),
                  ),
                  "application/msword",
                )
              }
            >
              Download memo (Word)
            </button>
          </div>
        </>,
      );
    }

    case "ro_utilization_trend": {
      if (!month) return frame(scopeNote("No month selected."));
      const m = memo(records, month);
      return frame(
        <>
          {scopeNote(
            "RO deliveries as a percentage of RO removals. Above 100% draws the yard down (negative Net RO Yard).",
          )}
          <Table
            head={["Month", "RO Deliveries", "RO Removals", "Utilization Rate", "Net RO Yard"]}
            rows={m.roTrend.map((r) => [
              monthLabel(r.month),
              fmtInt(r.ro_delivery),
              fmtInt(r.ro_removal),
              r.ro_utilization === null ? "n/a" : `${r.ro_utilization}%`,
              <Signed key="n" n={r.net_ro_yard} />,
            ])}
          />
        </>,
      );
    }

    case "net_fl_placed_trend": {
      if (!month) return frame(scopeNote("No month selected."));
      const m = memo(records, month);
      return frame(
        <>
          {scopeNote("FL deliveries minus FL removals, the client's own sign on this table.")}
          <Table
            head={["Month", "FL Deliveries", "FL Removals", "Net FL Placed"]}
            rows={m.flTrend.map((r) => [
              monthLabel(r.month),
              fmtInt(r.fl_delivery),
              fmtInt(r.fl_removal),
              <Signed key="n" n={r.net_fl_placed} />,
            ])}
          />
        </>,
      );
    }

    case "daily_tracker":
      return (
        <DailyTracker
          records={records}
          month={month}
          detail={detail}
          frame={frame}
          scopeNote={scopeNote}
        />
      );

    case "work_order_detail":
      return <WorkOrderDetail month={month} detail={detail} frame={frame} scopeNote={scopeNote} />;

    default:
      return <ErrorState message={`Unknown container view '${view}' on ${widget.id}`} />;
  }
}

type Frame = (node: ReactNode, opts?: { heading?: boolean }) => ReactNode;

function siteGate(detail: DetailState): ReactNode | null {
  if (detail.kind === "loading")
    return <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading sites...</div>;
  if (detail.kind === "error")
    return <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{GATE_COPY}</div>;
  const state = siteColumnState(detail.columnsPresent, detail.detail);
  if (state === "ready") return null;
  return (
    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
      {declaredFeedGap(SITE_FEED)?.copy ?? GATE_COPY}
    </div>
  );
}

function DailyTracker(props: {
  records: ContainerRecord[];
  month: string | null;
  detail: DetailState;
  frame: Frame;
  scopeNote: (text: string) => ReactNode;
}) {
  const { records, month, detail, frame, scopeNote } = props;
  const dates = useMemo(
    () => [...new Set(records.filter((r) => r.mk === month).map((r) => r.date))].sort().reverse(),
    [records, month],
  );
  const [picked, setPicked] = useState<string | null>(null);
  const date =
    picked && dates.includes(picked) ? picked : month ? latestActiveDate(records, month) : null;
  if (!date)
    return (
      <>{frame(scopeNote(`${month ? monthLabel(month) : ""}: no container work orders loaded.`))}</>
    );
  const c = dailyCounts(records, date);
  const gate = siteGate(detail);
  const sites = detail.kind === "ready" ? detail.detail.filter((r) => r.date === date) : [];
  return (
    <>
      {frame(
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <label style={{ fontSize: 12, color: "var(--text-2)" }} htmlFor="container-daily-date">
              Scheduled date
            </label>
            <select
              id="container-daily-date"
              value={date}
              onChange={(e) => setPicked(e.target.value)}
              style={{ fontSize: 12, padding: "3px 6px" }}
            >
              {dates.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <Table
            head={["Date", "RO Del", "RO Rem", "FL Del", "FL Rem", "Other FL", "Total"]}
            rows={[
              [
                c.date,
                fmtInt(c.ro_delivery),
                fmtInt(c.ro_removal),
                fmtInt(c.fl_delivery),
                fmtInt(c.fl_removal),
                fmtInt(c.other),
                fmtInt(c.total),
              ],
            ]}
          />
          <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700 }}>Sites</div>
          {gate ?? (
            <Table
              head={["Site", "Site Id", "Division", "Line", "Type", "Size"]}
              rows={sites.map((r) => [
                r.site_name || "-",
                r.site_id || "-",
                r.div,
                LOB_SHORT[r.lob],
                r.work_type,
                r.size,
              ])}
            />
          )}
        </>,
      )}
    </>
  );
}

function WorkOrderDetail(props: {
  month: string | null;
  detail: DetailState;
  frame: Frame;
  scopeNote: (text: string) => ReactNode;
}) {
  const { month, detail, frame, scopeNote } = props;
  const gate = siteGate(detail);
  if (gate || detail.kind !== "ready") return <>{frame(gate)}</>;
  const groups: { title: string; lob: ContainerLob; role: string }[] = [
    { title: "RO Deliveries", lob: "roll_off", role: "delivery" },
    { title: "RO Removals", lob: "roll_off", role: "removal" },
    { title: "FL Deliveries", lob: "front_load", role: "delivery" },
    { title: "FL Removals", lob: "front_load", role: "removal" },
  ];
  return (
    <>
      {frame(
        <>
          {scopeNote(`${month ? monthLabel(month) : ""}. One row per completed work order.`)}
          <button
            type="button"
            style={{ ...buttonStyle, marginBottom: 10 }}
            onClick={() =>
              download(
                `container-work-orders-${month}.csv`,
                workOrderLogCsv(detail.detail),
                "text/csv",
              )
            }
          >
            Download work order log (CSV)
          </button>
          {groups.map((g) => {
            const rows = detail.detail.filter((r) => r.lob === g.lob && r.role === g.role);
            return (
              <div key={g.title} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  {g.title} ({fmtInt(rows.length)})
                </div>
                {rows.length === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>None this month.</div>
                ) : (
                  <Table
                    head={["Work Order", "Date", "Division", "Size", "Site", "Site Id"]}
                    rows={rows.map((r) => [
                      r.work_order,
                      r.date,
                      r.div,
                      r.size,
                      r.site_name || "-",
                      r.site_id || "-",
                    ])}
                  />
                )}
              </div>
            );
          })}
        </>,
      )}
    </>
  );
}

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** The memo as a Word-openable HTML document (the client's tool does the same). */
function memoDocument(
  m: ReturnType<typeof memo>,
  rows: (ReturnType<typeof memo>["combined"] & { util: string })[],
): string {
  const sign = (n: number) => `${n > 0 ? "+" : ""}${n}`;
  const u = (x: number | null) => (x === null ? "n/a" : `${x}%`);
  const table = (head: string[], body: (string | number)[][]) =>
    `<table border="1" cellspacing="0" cellpadding="4"><thead><tr>${head
      .map((h) => `<th>${esc(h)}</th>`)
      .join("")}</tr></thead><tbody>${body
      .map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`)
      .join("")}</tbody></table>`;
  return `<html><head><meta charset="utf-8"><title>Container Inventory Memo</title></head><body style="font-family:Calibri,Arial,sans-serif">
<h1>Container Inventory</h1><p>Board Memorandum, ${esc(monthLabel(m.month))}</p>
<h2>Summary</h2><p>${esc(m.summary)}</p>
<h2>${esc(monthLabel(m.month))} Container Activity</h2>
${table(
  ["Division", "RO Del", "RO Rem", "RO Utilization", "FL Del", "FL Rem", "Net FL Placed", "Total"],
  rows.map((r) => [
    r.division,
    r.ro_delivery,
    r.ro_removal,
    r.util,
    r.fl_delivery,
    r.fl_removal,
    sign(r.net_fl_placed),
    r.total,
  ]),
)}
<p><i>Net FL Placed = FL deliveries minus FL removals (the client's label).</i></p>
<h2>RO Utilization Rate</h2>
${table(
  ["Month", "RO Deliveries", "RO Removals", "Utilization Rate", "Net RO Yard"],
  m.roTrend.map((r) => [
    monthLabel(r.month),
    r.ro_delivery,
    r.ro_removal,
    u(r.ro_utilization),
    sign(r.net_ro_yard),
  ]),
)}
<p><i>Net RO Yard = RO removals minus RO deliveries (containers back to the yard).</i></p>
<h2>FEL Container Activity</h2>
${table(
  ["Month", "FL Deliveries", "FL Removals", "Net FL Placed"],
  m.flTrend.map((r) => [monthLabel(r.month), r.fl_delivery, r.fl_removal, sign(r.net_fl_placed)]),
)}
<p><i>Net FL Placed = FL deliveries minus FL removals (the client's label).</i></p>
<h2>Current Yard Inventory, Physical Count</h2>
<p><i>Not available - awaiting a yard count entered by staff.</i></p>
</body></html>`;
}
